import { ReactElement, useMemo, useRef, useState } from 'react'
import { clipPointsToPolygon, getLineCoordinateSystem } from '@editorUtils'
import {
  DrawControllerWrapper,
  useTapelineCentersSnapTargets,
  useTapelineSnapTargets,
} from '@scene'
import { find, inRange, some } from 'lodash-es'
import { Plane, Vector2 } from 'three'
import { v4 as uuid } from 'uuid'
import { useTheme } from '@mui/material'
import { DEFAULT_LINE_LIKE_SETTINGS } from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import {
  DraggableLine,
  DraggableLineRef,
  LineHandles,
  DrawControllerRef,
  TransientDrawState,
  getOrientedWorldNormalsForIntersection,
  ImmutableVector3,
  guidFromDrawControllerEvent,
} from '@modugen/ui-toolkit'
import { useEditElementStore, useControlStore, useModelStore, roofStoreyKey } from '@editorStores'
import { useClickActionEnabled } from '@editorHooks'
import { useStructuralPlanningDrawerEsc } from '@structuralPlanningHooks'
import {
  SnapTargetElementType,
  useDefault2DSnapConfigForElementType,
} from 'src/components/pages/Editor/hooks/useDefaultSnapConfigForElementType'
import getPlaneLineIntersection from 'src/components/pages/Editor/utils/getPlaneLineIntersection'
import { roundXYImmutableVector3 } from 'src/geometry/rounding'
import { purlinEqualnessFactor } from '../../../PurlinsDrawing/components/PurlinForm/schema'
import { useModelSnapTargets } from '../../hooks'
import { PurlinElements } from '../Purlin2D'
import { NUM_DECIMALS_ROUND_PURLIN_DRAWER_2D } from './constants'

const minPurlinLength = 0.1

const PurlinDrawer2D = (): ReactElement => {
  const { scenePalette } = useTheme()

  const drawControllerRef = useRef<DrawControllerRef>(null)
  const lineRef = useRef<DraggableLineRef>(null)

  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const snapToAngles = useControlStore(state => state.snapToAngles)
  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)

  const modelSnapTargets = useModelSnapTargets({ xyOnly: true })
  const tapelineSnapTargets = useTapelineSnapTargets()
  const tapelineCenterTargets = useTapelineCentersSnapTargets()
  const additionalSnapTargets = useMemo(
    () => [...modelSnapTargets, ...tapelineSnapTargets, ...tapelineCenterTargets],
    [modelSnapTargets, tapelineSnapTargets, tapelineCenterTargets],
  )

  const addPurlin = useModelStore(state => state.addPurlin)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)

  const model = useModelStore(state => state.model)

  const drawTargetGuids = useMemo(
    () => new Set([...model.roof_slabs, ...model.slabs].map(({ guid }) => guid)),
    [model],
  )

  const [drawingActive, setDrawingActive] = useState(false)

  // EVENTS

  const onDrawStart = (state: TransientDrawState) => {
    const { drawPoint, drawTarget } = state

    if (lineRef.current && drawPoint && drawTarget) {
      setDrawingActive(true)
      lineRef.current?.setHandleAndStartDragging(LineHandles.End, drawPoint, drawPoint)

      // the angle snapping origin needs to be defined outside of the draw
      // controller to allow for flexibility (see onLineEditStart)

      if (drawPoint && drawTarget) {
        // define the plane angular snapping needs to work inside
        const snappingNormals = getOrientedWorldNormalsForIntersection(drawTarget, true)

        if (snappingNormals) {
          drawControllerRef.current?.setAngleSnappingOrigin({
            origin: drawPoint,
            ...snappingNormals,
          })
        }
      }
    }
  }

  const onDrawEnd = (transientDrawState: TransientDrawState) => {
    setDrawingActive(false)

    if (lineRef?.current && transientDrawState.drawTarget) {
      const drawnLine = lineRef.current.stopDraggingAndGetLine()
      const drawnLineStart = drawnLine.start
      const drawnLineEnd = drawnLine.end
      const roundedStart = roundXYImmutableVector3(
        drawnLineStart,
        NUM_DECIMALS_ROUND_PURLIN_DRAWER_2D,
      )
      const roundedEnd = roundXYImmutableVector3(drawnLineEnd, NUM_DECIMALS_ROUND_PURLIN_DRAWER_2D)

      const guid = guidFromDrawControllerEvent(transientDrawState)
      if (!guid) throw new Error('Guid on object could not be found')

      const originalRoofSlab = find([...model.roof_slabs, ...model.slabs], { guid }) as ShapeObject

      const roofSlabPoints = originalRoofSlab.shape.points

      const originalPlane = new Plane().setFromCoplanarPoints(
        roofSlabPoints[0].v,
        roofSlabPoints[1].v,
        roofSlabPoints[2].v,
      )

      const projectedStart = getPlaneLineIntersection(
        originalPlane,
        new Vector2(roundedStart.x, roundedStart.y),
      ) as ImmutableVector3
      const projectedEnd = getPlaneLineIntersection(
        originalPlane,
        new Vector2(roundedEnd.x, roundedEnd.y),
      ) as ImmutableVector3

      if (roundedStart.distanceTo(roundedEnd) >= minPurlinLength) {
        const threshold = 0.0001

        // here we ensure the polygon start/end will be the same (i.e. the
        // purlin being exactly horizontal) IF z of start and end are close to
        // each other
        const zStart = projectedStart.z
        let zEnd = projectedEnd.z
        if (inRange(zStart, zEnd - threshold, zEnd + threshold)) {
          zEnd = projectedStart.z
        }

        // this will ensure the purlin never exceeds the edges of the original
        // roof slab
        const [clippedStart] = clipPointsToPolygon(roofSlabPoints, [
          new ImmutableVector3(projectedStart.x, projectedStart.y, zStart),
        ])
        const [clippedEnd] = clipPointsToPolygon(roofSlabPoints, [
          new ImmutableVector3(projectedEnd.x, projectedEnd.y, zEnd),
        ])

        const isHorizontal =
          clippedStart.z.toFixed(purlinEqualnessFactor) ===
          clippedEnd.z.toFixed(purlinEqualnessFactor)

        // in order to place the purlin on the exact same height and therefore
        // prevent any errors in the backend we have to move the end to the same
        // height if both ends are similar to each other
        const zEndFinal = isHorizontal ? clippedStart.z : clippedEnd.z

        const start = clippedStart
        const end = new ImmutableVector3(clippedEnd.x, clippedEnd.y, zEndFinal)

        const purlin: PlanLineElement = {
          ...DEFAULT_LINE_LIKE_SETTINGS,
          guid: uuid(),
          shape: {
            start,
            end,
          },
          storey: roofStoreyKey,
          is_local: true,
          coordinate_system: getLineCoordinateSystem(start, end),
          element_type: 'purlins' as ElementTypes,
        }

        addPurlin(purlin)
        setActiveElement(purlin.guid)
      }
    }
  }

  const onDrawMouseMove = (state: TransientDrawState) => {
    if (state.drawPoint) {
      lineRef.current?.updateActiveHandle(state.drawPoint)
    }
  }

  useStructuralPlanningDrawerEsc(() => {
    drawControllerRef.current?.abortDrawing()
    lineRef.current?.stopDraggingAndGetLine()

    // not so nice way to reset the lineref to it's defaults

    lineRef.current?.setHandleAndStartDragging(
      LineHandles.Start,
      new ImmutableVector3(0, 0, 1000),
      new ImmutableVector3(0, 0, 1001),
    )
    lineRef.current?.stopDraggingAndGetLine()
  }, drawingActive)

  // EFFECTS
  useDefault2DSnapConfigForElementType(SnapTargetElementType.PURLINS)

  const drawControllerEnabled = useClickActionEnabled()

  return (
    <>
      <DrawControllerWrapper
        ref={drawControllerRef}
        enabled={drawControllerEnabled}
        snapToCornersAndEdges={snapToCornersAndEdges}
        snapToAngles={snapToAngles}
        orthoSnap={snapOrthogonal}
        enableIndicator
        additionalSnapTargets={additionalSnapTargets}
        onDrawStart={onDrawStart}
        onMouseMove={onDrawMouseMove}
        onDrawEnd={onDrawEnd}
        isValidDrawStart={intersections => {
          return !some(intersections, intersection => {
            if ('type' in intersection.object.userData) {
              return intersection.object.userData.type === PurlinElements.Line
            }
          })
        }}
        xyOnly
        indicatorType="crosshair"
        isValidDrawTarget={object => {
          const isRoof = drawTargetGuids.has(object.name)
          if (isRoof) return true

          if ('instanceIdToId' in object.userData) {
            const ids = Object.values(object.userData.instanceIdToId) as string[]

            const validTarget = some(ids, id => drawTargetGuids.has(id))

            return validTarget
          }

          return false
        }}
      />

      {/* TEMP LINE */}

      <DraggableLine
        ref={lineRef}
        line={{ start: new ImmutableVector3(0, 0, 1000), end: new ImmutableVector3(0, 0, 1001) }}
        lineName={PurlinElements.Line}
        handleName={PurlinElements.Handle}
        color={scenePalette.elements3d.beams as string}
        showIndicators={false}
        lineThreeProps={{
          userData: {
            type: PurlinElements.Line,
          },
        }}
      />
    </>
  )
}

export default PurlinDrawer2D
